import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  X,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Info,
  Trash2,
  RefreshCw,
  Minus,
  ArrowRight,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { FORMATO_UL_OPTIONS, ImportRow, Regional, RegionalBatchItem, REGIAO_UL_DROPDOWN_OPTIONS } from './types';

type ImportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: (rows: RegionalBatchItem[]) => void | Promise<void>;
  currentRegionais: Regional[];
};

type FilterTab = 'all' | 'changed' | 'unchanged' | 'invalid';

function ChangeTypeBadge({ type }: { type: string }) {
  if (type === 'changed') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
        <RefreshCw className="h-3 w-3" />
        Alterado
      </span>
    );
  }

  if (type === 'unchanged') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        <Minus className="h-3 w-3" />
        Sem alteração
      </span>
    );
  }

  return null;
}

function ValueTransition({ currentValue, newValue }: { currentValue: string; newValue: string }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="inline-flex items-center rounded-md border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
        {currentValue || '(vazio)'}
      </span>
      <ArrowRight className="h-3 w-3 flex-shrink-0 text-amber-500" />
      <span className="inline-flex items-center rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
        {newValue}
      </span>
    </div>
  );
}

function FieldPreview({
  valid,
  value,
  currentValue,
}: {
  valid: boolean;
  value: string;
  currentValue?: string;
}) {
  if (!valid) return <span className="text-gray-400">{value || '-'}</span>;

  if (!value) return <span className="text-[11px] text-gray-400">Sem alteração</span>;

  if ((currentValue || '') !== value) {
    return <ValueTransition currentValue={currentValue || ''} newValue={value} />;
  }

  return (
    <span className="inline-flex items-center rounded-md border border-emerald-100 bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">
      {value}
    </span>
  );
}

const ImportPreviewTable: React.FC<{ rows: ImportRow[] }> = ({ rows }) => {
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');

  const counts = useMemo(() => {
    const valid = rows.filter((row) => row.valid);
    const invalid = rows.filter((row) => !row.valid);
    return {
      all: rows.length,
      changed: valid.filter((row) => row.changeType === 'changed').length,
      unchanged: valid.filter((row) => row.changeType === 'unchanged').length,
      invalid: invalid.length,
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    switch (activeFilter) {
      case 'changed':
        return rows.filter((row) => row.valid && row.changeType === 'changed');
      case 'unchanged':
        return rows.filter((row) => row.valid && row.changeType === 'unchanged');
      case 'invalid':
        return rows.filter((row) => !row.valid);
      default:
        return rows;
    }
  }, [activeFilter, rows]);

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: 'all', label: 'Todos', count: counts.all },
    { key: 'changed', label: 'Alterados', count: counts.changed },
    { key: 'unchanged', label: 'Sem alteração', count: counts.unchanged },
    { key: 'invalid', label: 'Inválidos', count: counts.invalid },
  ];

  const getRowBg = (row: ImportRow) => {
    if (!row.valid) return 'bg-red-50/50';
    if (row.changeType === 'changed') return 'bg-amber-50/40';
    return 'bg-white';
  };

  return (
    <div className="flex min-h-0 flex-col gap-3">
      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Filtrar itens da importação">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeFilter === tab.key}
            onClick={() => setActiveFilter(tab.key)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
              activeFilter === tab.key
                ? 'bg-gray-900 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
            <span
              className={`inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold ${
                activeFilter === tab.key ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
              }`}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      <div className="min-h-0 overflow-hidden rounded-xl border border-gray-200">
        <div className="max-h-[360px] overflow-y-auto">
          <table className="min-w-full text-xs">
            <thead className="sticky top-0 z-10 bg-gray-50 shadow-[0_1px_0_0_theme(colors.gray.200)]">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider text-gray-500">ID Distribuidor</th>
                <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider text-gray-500">Nome</th>
                <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider text-gray-500">Regional UL</th>
                <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider text-gray-500">Formato UL</th>
                <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider text-gray-500">Info</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-400">
                    Nenhum item nesta categoria
                  </td>
                </tr>
              )}

              {filteredRows.map((row, index) => (
                <tr key={index} className={getRowBg(row)}>
                  <td className="px-3 py-2.5">
                    {!row.valid ? (
                      <AlertCircle className="h-4 w-4 text-red-400" />
                    ) : row.changeType ? (
                      <ChangeTypeBadge type={row.changeType} />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-gray-700">{row.idDistribuidor || '-'}</td>
                  <td className="px-3 py-2.5 text-gray-700">{row.nomeDistribuidor || '-'}</td>
                  <td className="px-3 py-2.5">
                    <FieldPreview valid={row.valid} value={row.regiaoUL} currentValue={row.currentRegiaoUL} />
                  </td>
                  <td className="px-3 py-2.5">
                    <FieldPreview valid={row.valid} value={row.formatoUL} currentValue={row.currentFormatoUL} />
                  </td>
                  <td className="px-3 py-2.5">
                    {!row.valid ? (
                      <span className="text-red-600">{row.reason}</span>
                    ) : row.changeType === 'unchanged' ? (
                      <span className="text-[11px] text-gray-400">Valores já são os mesmos</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

function normalizeKey(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function canonicalizeOption(value: string, options: readonly string[]) {
  const text = value.trim();
  if (!text) return '';
  return options.find((option) => normalizeKey(option) === normalizeKey(text)) || null;
}

function parseFile(file: File): Promise<ImportRow[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json: string[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (json.length < 2) {
          reject(new Error('A planilha deve conter ao menos o cabeçalho e uma linha de dados.'));
          return;
        }

        const rows = json.slice(1);
        const parsed: ImportRow[] = rows.map((row) => {
          const idDistribuidor = (row[0] || '').toString().trim();
          const rawRegiaoUL = (row[1] || '').toString().trim();
          const rawFormatoUL = (row[2] || '').toString().trim();
          const regiaoUL = canonicalizeOption(rawRegiaoUL, REGIAO_UL_DROPDOWN_OPTIONS);
          const formatoUL = canonicalizeOption(rawFormatoUL, FORMATO_UL_OPTIONS);

          if (!idDistribuidor && !rawRegiaoUL && !rawFormatoUL) {
            return { idDistribuidor, nomeDistribuidor: '', regiaoUL: '', formatoUL: '', valid: false, reason: 'Linha vazia' };
          }

          if (!idDistribuidor) {
            return { idDistribuidor, nomeDistribuidor: '', regiaoUL: regiaoUL || rawRegiaoUL, formatoUL: formatoUL || rawFormatoUL, valid: false, reason: 'ID Distribuidor vazio' };
          }

          if (!rawRegiaoUL && !rawFormatoUL) {
            return { idDistribuidor, nomeDistribuidor: '', regiaoUL: '', formatoUL: '', valid: false, reason: 'Informe Regional UL ou Formato UL' };
          }

          if (rawRegiaoUL && regiaoUL === null) {
            return {
              idDistribuidor,
              nomeDistribuidor: '',
              regiaoUL: rawRegiaoUL,
              formatoUL: formatoUL || rawFormatoUL,
              valid: false,
              reason: `Regional UL inválida: "${rawRegiaoUL}"`,
            };
          }

          if (rawFormatoUL && formatoUL === null) {
            return {
              idDistribuidor,
              nomeDistribuidor: '',
              regiaoUL: regiaoUL || rawRegiaoUL,
              formatoUL: rawFormatoUL,
              valid: false,
              reason: `Formato UL inválido: "${rawFormatoUL}"`,
            };
          }

          return { idDistribuidor, nomeDistribuidor: '', regiaoUL: regiaoUL || '', formatoUL: formatoUL || '', valid: true };
        });

        const filtered = parsed.filter(
          (r) => !(r.reason === 'Linha vazia' && !r.idDistribuidor)
        );

        resolve(filtered);
      } catch {
        reject(new Error('Erro ao ler o arquivo. Verifique se é um XLSX válido.'));
      }
    };
    reader.onerror = () => reject(new Error('Erro ao ler o arquivo.'));
    reader.readAsArrayBuffer(file);
  });
}

function enrichRowsWithChanges(rows: ImportRow[], regionais: Regional[]): ImportRow[] {
  const regionMap = new Map<string, { regiaoUL: string; formatoUL: string; nome: string }>();
  regionais.forEach((r) => {
    regionMap.set(r.idDistribuidor, {
      regiaoUL: r.regiaoUL,
      formatoUL: r.formatoUL,
      nome: r.nomeCompletoDistribuidor,
    });
  });

  return rows.map((row) => {
    if (!row.valid) return row;

    const existing = regionMap.get(row.idDistribuidor);

    if (existing === undefined) {
      return { ...row, valid: false, reason: `ID "${row.idDistribuidor}" não encontrado na base` };
    }

    const regionChanged = Boolean(row.regiaoUL) && existing.regiaoUL !== row.regiaoUL;
    const formatChanged = Boolean(row.formatoUL) && existing.formatoUL !== row.formatoUL;

    if (!regionChanged && !formatChanged) {
      return {
        ...row,
        nomeDistribuidor: existing.nome,
        changeType: 'unchanged',
        currentRegiaoUL: existing.regiaoUL,
        currentFormatoUL: existing.formatoUL,
      };
    }

    return {
      ...row,
      nomeDistribuidor: existing.nome,
      changeType: 'changed',
      currentRegiaoUL: existing.regiaoUL,
      currentFormatoUL: existing.formatoUL,
    };
  });
}

export const ImportModal: React.FC<ImportModalProps> = ({
  isOpen,
  onClose,
  onImportComplete,
  currentRegionais,
}) => {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parsedRows, setParsedRows] = useState<ImportRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setFile(null);
    setParsedRows([]);
    setParseError(null);
    setImporting(false);
    setImportDone(false);
    setImportErrors([]);
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      resetState();
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen, resetState]);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !importing) onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose, importing]);

  const handleClose = () => {
    if (importing) return;
    resetState();
    onClose();
  };

  const handleFile = async (f: File) => {
    setParseError(null);
    setImportDone(false);
    setImportErrors([]);

    if (!f.name.endsWith('.xlsx') && !f.name.endsWith('.xls')) {
      setParseError('Formato inválido. Envie um arquivo .xlsx');
      return;
    }

    setFile(f);
    try {
      const rows = await parseFile(f);
      const enriched = enrichRowsWithChanges(rows, currentRegionais);
      setParsedRows(enriched);
    } catch (err: any) {
      setParseError(err.message || 'Erro ao processar arquivo.');
      setParsedRows([]);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    if (inputRef.current) inputRef.current.value = '';
  };

  const validRows = parsedRows.filter((r) => r.valid);
  const invalidRows = parsedRows.filter((r) => !r.valid);

  const changeCounts = useMemo(() => {
    const changed = validRows.filter((r) => r.changeType === 'changed').length;
    const unchanged = validRows.filter((r) => r.changeType === 'unchanged').length;
    return { changed, unchanged };
  }, [validRows]);

  const handleImport = async () => {
    const rowsToSend = validRows.filter((r) => r.changeType === 'changed');
    if (rowsToSend.length === 0) return;
    setImporting(true);
    setImportErrors([]);

    try {
      const items = rowsToSend.map((r) => {
        const item: RegionalBatchItem = {
          id_distribuidor: r.idDistribuidor,
        };

        if (r.regiaoUL) item.regional_ul = r.regiaoUL;
        if (r.formatoUL) item.formato_ul = r.formatoUL;

        return item;
      });

      await onImportComplete(items);
    } catch (err: any) {
      setImportErrors([err?.response?.data?.error || 'Falha na conexão com o servidor']);
    }

    setImporting(false);
    setImportDone(true);
  };

  const importableCount = changeCounts.changed;
  const importSuccessCount = importDone ? importableCount - importErrors.length : 0;

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Importar planilha"
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div
        className={`relative bg-white rounded-2xl shadow-2xl w-[95vw] max-w-4xl max-h-[90vh] flex flex-col transition-all duration-300 ${
          visible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Importar Regionais</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Faça upload de um arquivo XLSX com regional e/ou formato UL
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={importing}
            className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-800 space-y-2">
                <p className="font-medium">Padrão da planilha esperado:</p>
                <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-blue-100/50">
                        <th className="px-3 py-2 text-left font-semibold text-blue-900">IDDistribuidor</th>
                        <th className="px-3 py-2 text-left font-semibold text-blue-900">RegionalUL</th>
                        <th className="px-3 py-2 text-left font-semibold text-blue-900">FormatoUL</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-blue-100">
                        <td className="px-3 py-1.5 text-blue-700 font-mono">150</td>
                        <td className="px-3 py-1.5 text-blue-700">Sul</td>
                        <td className="px-3 py-1.5 text-blue-700">AE</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p>
                  Regionais válidas: <strong>Sul</strong>, <strong>São Paulo</strong>, <strong>Centro-Norte</strong>, <strong>Leste</strong>, <strong>Nordeste</strong>.
                </p>
                <p>
                  Formatos válidos: <strong>AE</strong>, <strong>AT</strong>, <strong>AT FARMA</strong>, <strong>E-UB</strong>, <strong>Cash Carry Regular</strong>, <strong>NC</strong>.
                </p>
              </div>
            </div>
          </div>

          {!file && !importing && !importDone && (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => inputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer transition-all ${
                isDragging
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
              }`}
              role="button"
              aria-label="Área de upload de arquivo"
            >
              <div
                className={`p-3 rounded-xl mb-3 ${
                  isDragging ? 'bg-blue-100' : 'bg-gray-200'
                }`}
              >
                <Upload
                  className={`w-6 h-6 ${isDragging ? 'text-blue-600' : 'text-gray-500'}`}
                />
              </div>
              <p className="text-sm font-medium text-gray-700">
                {isDragging ? 'Solte o arquivo aqui' : 'Arraste ou clique para selecionar'}
              </p>
              <p className="text-xs text-gray-400 mt-1">Apenas arquivos .xlsx</p>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleInputChange}
                className="hidden"
                aria-hidden="true"
              />
            </div>
          )}

          {parseError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700">{parseError}</p>
            </div>
          )}

          {file && parsedRows.length > 0 && !importDone && (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                    {file.name}
                  </span>
                </div>
                {!importing && (
                  <button
                    onClick={resetState}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    Remover
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <div>
                    <p className="text-base font-bold text-emerald-700 leading-tight">{validRows.length}</p>
                    <p className="text-[10px] text-emerald-600">Válidos</p>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2.5">
                  <RefreshCw className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="text-base font-bold text-amber-700 leading-tight">{changeCounts.changed}</p>
                    <p className="text-[10px] text-amber-600">Serão alterados</p>
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center gap-2.5">
                  <Minus className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <div>
                    <p className="text-base font-bold text-gray-600 leading-tight">{changeCounts.unchanged}</p>
                    <p className="text-[10px] text-gray-500">Sem alteração</p>
                  </div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2.5">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <div>
                    <p className="text-base font-bold text-red-700 leading-tight">{invalidRows.length}</p>
                    <p className="text-[10px] text-red-600">Inválidos</p>
                  </div>
                </div>
              </div>

              {importing && (
                <div className="flex items-center justify-center gap-2 py-4">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                  <span className="text-sm text-gray-600">Importando...</span>
                </div>
              )}

              <ImportPreviewTable rows={parsedRows} />
            </>
          )}

          {importDone && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className={`rounded-full p-4 mb-4 ${importErrors.length > 0 ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                {importErrors.length > 0 ? (
                  <AlertCircle className="w-8 h-8 text-amber-600" />
                ) : (
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                )}
              </div>
              <p className="text-base font-semibold text-gray-900">
                {importErrors.length > 0 ? 'Importação concluída com erros' : 'Importação concluída'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {importSuccessCount} {importSuccessCount === 1 ? 'item importado' : 'itens importados'} com sucesso
              </p>
              {importErrors.length > 0 && (
                <div className="mt-4 w-full bg-red-50 border border-red-200 rounded-xl p-3 max-h-[120px] overflow-y-auto">
                  {importErrors.map((err, i) => (
                    <p key={i} className="text-xs text-red-700">{err}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <span className="text-xs text-gray-400">
            {!importing && 'Pressione ESC para fechar'}
          </span>
          <div className="flex items-center gap-3">
            {!importDone && (
              <button
                onClick={handleClose}
                disabled={importing}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
            )}
            {file && parsedRows.length > 0 && !importDone && (
              <button
                onClick={handleImport}
                disabled={importableCount === 0 || importing}
                className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-blue-600/20 disabled:shadow-none"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Importar {importableCount} {importableCount === 1 ? 'item' : 'itens'}
                  </>
                )}
              </button>
            )}
            {importDone && (
              <button
                onClick={handleClose}
                className="px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-all shadow-sm shadow-blue-600/20"
              >
                Fechar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
